/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/* =========================================================
   VFR PRODUCT RELEASE FACTS

   Finds UPC, Label, Release Date and Format at the bottom
   of the product description and displays detected fields
   inside the product purchase panel.

   Supports:
   - All four fields
   - Only one, two or three fields
   - Labels with or without colons
   - Shopify product rerenders
   ========================================================= */

(() => {
  const FIELD_CONFIG = {
    releaseDate: {
      label: "Release Date",
      className: "release-date"
    },
    label: {
      label: "Label",
      className: "label"
    },
    format: {
      label: "Format",
      className: "format"
    },
    upc: {
      label: "UPC",
      className: "upc"
    }
  };

  const DISPLAY_ORDER = [
    "releaseDate",
    "label",
    "format",
    "upc"
  ];

  const FIELD_LABEL_PATTERN =
    "(?:Release\\s*Date|UPC|Label|Format)";

  const createFieldRegex = () =>
    new RegExp(
      "\\b(Release\\s*Date|UPC|Label|Format)\\b" +
        "\\s*:?\\s*" +
        "([\\s\\S]*?)" +
        "(?=\\s*\\b" +
        FIELD_LABEL_PATTERN +
        "\\b\\s*:?|$)",
      "gi"
    );

  function cleanText(value) {
    return String(value || "")
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+/g, " ")
      .replace(/\s*\n\s*/g, "\n")
      .replace(/\n{2,}/g, "\n")
      .replace(/^[\s:;|,.\-–—]+/, "")
      .replace(/[\s:;|,.\-–—]+$/, "")
      .trim();
  }

  /*
   * textContent does not reliably preserve <br> elements.
   * This creates a text version with useful line boundaries.
   */
  function getTextWithBreaks(element) {
    const clone = element.cloneNode(true);

    clone.querySelectorAll("br").forEach((br) => {
      br.replaceWith("\n");
    });

    clone
      .querySelectorAll("p, div, li, tr, td, dd, dt")
      .forEach((block) => {
        block.append("\n");
      });

    return cleanText(clone.textContent);
  }

  function getFieldKey(rawLabel) {
    const normalized = cleanText(rawLabel)
      .toLowerCase()
      .replace(/\s+/g, "");

    if (normalized === "releasedate") {
      return "releaseDate";
    }

    if (normalized === "upc") {
      return "upc";
    }

    if (normalized === "label") {
      return "label";
    }

    if (normalized === "format") {
      return "format";
    }

    return null;
  }

  function parseMetadataElement(element) {
    const text = getTextWithBreaks(element);
    const fields = {};
    const regex = createFieldRegex();

    let match;

    while ((match = regex.exec(text)) !== null) {
      const key = getFieldKey(match[1]);
      const value = cleanText(match[2]);

      /*
       * Reject empty or suspiciously long matches.
       * Real values should be short.
       */
      if (
        key &&
        value &&
        value.length <= 160 &&
        !fields[key]
      ) {
        fields[key] = value;
      }
    }

    const foundCount = Object.keys(fields).length;

    /*
     * Determine whether the entire element consists only of
     * detected metadata. Only those elements are hidden later.
     */
    const leftoverText = text
      .replace(createFieldRegex(), "")
      .replace(/[\s:;|,.\-–—/]+/g, "");

    return {
      fields,
      metadataOnly:
        foundCount > 0 && leftoverText.length === 0
    };
  }

  function mergeCandidates(candidates, collectedFields, sourceNodes) {
    candidates.forEach((element) => {
      const result = parseMetadataElement(element);

      Object.entries(result.fields).forEach(([key, value]) => {
        if (!collectedFields[key]) {
          collectedFields[key] = value;
        }
      });

      if (
        result.metadataOnly &&
        Object.keys(result.fields).length > 0
      ) {
        sourceNodes.add(element);
      }
    });
  }

  function buildFactsElement(fields) {
    const list = document.createElement("dl");
    list.className = "vfr-product-facts";
    list.setAttribute(
      "aria-label",
      "Product release information"
    );

    DISPLAY_ORDER.forEach((key) => {
      const value = fields[key];

      if (!value) {
        return;
      }

      const config = FIELD_CONFIG[key];

      const item = document.createElement("div");
      item.className =
        "vfr-product-fact " +
        `vfr-product-fact--${config.className}`;

      const term = document.createElement("dt");
      term.className = "vfr-product-fact__label";
      term.textContent = config.label;

      const description = document.createElement("dd");
      description.className = "vfr-product-fact__value";
      description.textContent = value;

      item.append(term, description);
      list.append(item);
    });

    return list;
  }

  function renderProductFacts() {
    const productSection = document.querySelector(
      'main section[data-section-type="product"]'
    );

    if (!productSection) {
      return;
    }

    /*
     * Prevent duplicate insertion when payment widgets or
     * Shopify components update the page.
     */
    if (
      productSection.querySelector(".vfr-product-facts")
    ) {
      return;
    }

    const description = productSection.querySelector(
      ".product-block-list__item--description .rte"
    );

    const productMetaBlock = productSection.querySelector(
      ".product-info__block-item--product-meta"
    );

    if (!description || !productMetaBlock) {
      return;
    }

    const collectedFields = {};
    const sourceNodes = new Set();

    /*
     * First search Broadtime's generated area.
     */
    const broadtimeAreas = Array.from(
      description.querySelectorAll("[data-bt-autogen]")
    );

    broadtimeAreas.forEach((area) => {
      const metadataElements = Array.from(
        area.querySelectorAll("p, td, dd")
      ).slice(-12);

      mergeCandidates(
        metadataElements,
        collectedFields,
        sourceNodes
      );
    });

    /*
     * Some older products might not use data-bt-autogen.
     * Search only the final description elements as fallback.
     */
    if (Object.keys(collectedFields).length === 0) {
      const fallbackElements = Array.from(
        description.querySelectorAll("p, td, dd")
      ).slice(-8);

      mergeCandidates(
        fallbackElements,
        collectedFields,
        sourceNodes
      );
    }

    /*
     * Last-resort check for Broadtime data written directly
     * inside its container without a paragraph wrapper.
     */
    if (
      Object.keys(collectedFields).length === 0 &&
      broadtimeAreas.length > 0
    ) {
      mergeCandidates(
        broadtimeAreas.slice(-2),
        collectedFields,
        sourceNodes
      );
    }

    /*
     * No recognized metadata means no changes are made.
     */
    if (Object.keys(collectedFields).length === 0) {
      return;
    }

    const factsElement =
      buildFactsElement(collectedFields);

    const separator = productMetaBlock.querySelector(
      ".card__separator"
    );

    productMetaBlock.insertBefore(
      factsElement,
      separator || null
    );

    /*
     * Hide only clean metadata-only source elements.
     * Paragraphs containing descriptions or tracklists remain.
     */
    sourceNodes.forEach((element) => {
      element.classList.add(
        "vfr-product-facts-source--hidden"
      );
    });
  }

  function startProductFacts() {
    renderProductFacts();

    const productRerender = document.querySelector(
      "main product-rerender"
    );

    if (!productRerender) {
      return;
    }

    let renderTimer;

    const observer = new MutationObserver(() => {
      window.clearTimeout(renderTimer);

      renderTimer = window.setTimeout(() => {
        renderProductFacts();
      }, 60);
    });

    observer.observe(productRerender, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      startProductFacts,
      { once: true }
    );
  } else {
    startProductFacts();
  }

  document.addEventListener(
    "shopify:section:load",
    renderProductFacts
  );
})();
/* =========================================================
   VFR - ALIGN PRODUCT DESCRIPTION WITH MAIN IMAGE

   Desktop only. Measures the actual left edge of the main
   product image and applies equivalent padding inside the
   Description card.

   It does not move the outer Description layout container.
   ========================================================= */

(() => {
  const desktopMediaQuery = window.matchMedia(
    "(min-width: 1000px)"
  );

  let animationFrameId = null;

  function alignProductDescription() {
    const productSection = document.querySelector(
      'main section[data-section-type="product"]'
    );

    const gallery = productSection?.querySelector(
      ".product-block-list__item--gallery"
    );

    const description = productSection?.querySelector(
      ".product-block-list__item--description"
    );

    if (!description) {
      return;
    }

    if (!desktopMediaQuery.matches || !gallery) {
      description.style.removeProperty(
        "--vfr-description-content-offset"
      );

      return;
    }

    const mainImageBox =
      gallery.querySelector(
        ".product-gallery__carousel-item.is-selected " +
          ".product-gallery__size-limiter"
      ) ||
      gallery.querySelector(
        ".product-gallery__carousel-item.is-selected " +
          ".aspect-ratio"
      );

    if (!mainImageBox) {
      description.style.removeProperty(
        "--vfr-description-content-offset"
      );

      return;
    }

    /*
     * Measure from the Description container itself.
     * We only add padding inside it; we do not reposition it.
     */
    const descriptionRect =
      description.getBoundingClientRect();

    const imageRect =
      mainImageBox.getBoundingClientRect();

    const contentOffset = Math.max(
      0,
      Math.round(
        imageRect.left - descriptionRect.left
      )
    );

    description.style.setProperty(
      "--vfr-description-content-offset",
      `${contentOffset}px`
    );
  }

  function scheduleDescriptionAlignment() {
    if (animationFrameId !== null) {
      window.cancelAnimationFrame(
        animationFrameId
      );
    }

    animationFrameId =
      window.requestAnimationFrame(() => {
        alignProductDescription();
        animationFrameId = null;
      });
  }

  function startDescriptionAlignment() {
    scheduleDescriptionAlignment();

    window.addEventListener(
      "resize",
      scheduleDescriptionAlignment,
      { passive: true }
    );

    document.addEventListener(
      "click",
      (event) => {
        if (
          event.target.closest(
            ".product-gallery__thumbnail"
          )
        ) {
          window.setTimeout(
            scheduleDescriptionAlignment,
            100
          );
        }
      }
    );

    document.addEventListener(
      "variant:changed",
      scheduleDescriptionAlignment
    );

    document.addEventListener(
      "shopify:section:load",
      scheduleDescriptionAlignment
    );

    const productRerender =
      document.querySelector(
        "main product-rerender"
      );

    if (productRerender) {
      const observer = new MutationObserver(
        scheduleDescriptionAlignment
      );

      observer.observe(productRerender, {
        childList: true,
        subtree: true
      });
    }

    const mainImage = document.querySelector(
      "main .product-gallery__carousel-item.is-selected img"
    );

    if (mainImage && !mainImage.complete) {
      mainImage.addEventListener(
        "load",
        scheduleDescriptionAlignment,
        { once: true }
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      startDescriptionAlignment,
      { once: true }
    );
  } else {
    startDescriptionAlignment();
  }
})();
